import type { WorkflowStageDefinition } from "@/types"

/**
 * The OIO pipeline stages, keyed to the stage ids the backend actually emits
 * via copilotkit_emit_state (`current_stage` / `completed_stages`):
 *   ingest → alignment → analysis → speaking_order → section_draft → compile
 * Labels/substages are officer-facing phrasing for the progress tracker.
 */
export const OIO_STAGES: WorkflowStageDefinition[] = [
  {
    id: "ingest",
    label: "Reading the case documents",
    order: 1,
    substages: ["Loading the uploaded workspaces", "Validating the documents"],
  },
  {
    id: "alignment",
    label: "Mapping the allegations",
    order: 2,
    substages: ["Identifying each demand", "Mapping the amounts in dispute"],
  },
  {
    id: "analysis",
    label: "Analysing each allegation",
    order: 3,
    substages: [
      "Grounding the facts",
      "Weighing the evidence",
      "Assessing the burden of proof",
      "Researching the legal position",
    ],
  },
  {
    id: "speaking_order",
    label: "Checking the speaking order",
    order: 4,
    substages: ["Ensuring every demand is addressed", "Answering the reply's contentions"],
  },
  {
    id: "deliberation",
    label: "Deliberating the case as a whole",
    order: 5,
    substages: [
      "Weighing intent across the proceeding",
      "Ruling on procedural requests",
      "Reconciling head findings with the case-level view",
    ],
  },
  {
    id: "section_draft",
    label: "Drafting the order section by section",
    order: 6,
    substages: ["Brief facts", "Discussion & findings", "Operative order"],
  },
  {
    id: "compile",
    label: "Compiling the final order",
    order: 7,
    substages: ["Formatting", "Numbering the paragraphs"],
  },
]

/** Friendly labels for the per-allegation analysis sub-stages the agent streams. */
export const ANALYSIS_SUBSTAGE_LABELS: Record<string, string> = {
  fact_grounding: "Grounding the facts",
  corroboration: "Weighing corroborating evidence",
  burden_analysis: "Assessing the burden of proof",
  legal_corpus: "Researching the legal position",
  legal_research: "Researching the legal position",
  merged_legal_position: "Reconciling the legal position",
  reconciliation: "Reconciling the determination",
  section_draft: "Drafting the section",
}
